import { useEffect, useState } from 'react';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import { fetchAttachmentDetail, reprocessAttachment, type AiAttachmentDetail } from '../../lib/api';

interface AttachmentViewerProps {
  attachmentId: string | null;
  onClose: () => void;
}

const OCR_PROVIDERS = [
  { value: '', label: 'Default chain' },
  { value: 'paperless', label: 'Paperless' },
  { value: 'google-vision', label: 'Google Vision' },
  { value: 'ocrspace', label: 'OCR.space' },
  { value: 'tex-pdf', label: 'tex-pdf' },
];

export default function AttachmentViewer({ attachmentId, onClose }: AttachmentViewerProps) {
  const [detail, setDetail] = useState<AiAttachmentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [provider, setProvider] = useState('');

  useEffect(() => {
    if (!attachmentId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void fetchAttachmentDetail(attachmentId).then((res) => {
      if (cancelled) return;
      setDetail(res.ok && res.data ? res.data : null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [attachmentId]);

  const handleReprocess = async () => {
    if (!attachmentId) return;
    setReprocessing(true);
    const res = await reprocessAttachment(attachmentId, provider || undefined);
    setReprocessing(false);
    if (res.ok && res.data?.ok) {
      setDetail(res.data);
      toast.success(`Re-extracted ${res.data.extractedText.length} characters`);
    } else {
      toast.error(res.data?.error ?? res.error?.message ?? 'Re-run failed');
    }
  };

  const text = detail?.extractedText ?? '';

  return (
    <Modal
      open={Boolean(attachmentId)}
      onClose={onClose}
      title={detail?.filename ?? 'Attachment'}
      description={
        detail
          ? [
              detail.contentType || 'unknown type',
              detail.status,
              detail.shortRef ? `ref ${detail.shortRef}` : detail.referenceId ? `ref ${detail.referenceId.slice(-8)}` : null,
              detail.onServer || detail.ocisPath ? 'on server' : null,
              detail.ocisPath || null,
              detail.ocrProvider ? `OCR: ${detail.ocrProvider}` : null,
              detail.documentType ? `Classified: ${detail.documentType}` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      size="lg"
      footer={
        <div className="flex w-full items-center gap-2">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="rounded-lg border border-zt-border bg-zt-bg px-2 py-1.5 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
          >
            {OCR_PROVIDERS.map((p) => (
              <option key={p.value || 'default'} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleReprocess}
            disabled={reprocessing}
            className="flex items-center gap-1.5 rounded-lg border border-zt-border px-3 py-1.5 text-xs text-slate-200 hover:border-zt-primary hover:text-zt-primary disabled:opacity-50"
          >
            {reprocessing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            Re-run OCR
          </button>
          <button
            type="button"
            disabled={!text}
            onClick={() => {
              void navigator.clipboard.writeText(text);
              toast.success('Extracted text copied');
            }}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-zt-border px-3 py-1.5 text-xs text-slate-200 hover:border-zt-primary hover:text-zt-primary disabled:opacity-40"
          >
            <Copy size={13} /> Copy text
          </button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-zt-muted">
          <Loader2 size={14} className="animate-spin" /> Loading extraction…
        </div>
      )}
      {!loading && detail && (
        <div className="space-y-3">
          {detail.summary && (
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-wide text-zt-muted">Summary</p>
              <p className="text-sm text-slate-300">{detail.summary}</p>
            </div>
          )}
          {(detail.status === 'unavailable' || detail.bytesAvailable === false) && (
            <div className="rounded-lg border border-zt-warning/30 bg-zt-warning/5 px-3 py-2 text-xs text-zt-warning">
              {detail.unavailableReason ??
                'Original file is unavailable in storage. Extracted text below may still be usable.'}
            </div>
          )}
          <div>
            <p className="mb-1 text-[11px] uppercase tracking-wide text-zt-muted">
              Extracted text ({text.length} chars)
            </p>
            {text ? (
              <pre className="max-h-[45vh] overflow-auto whitespace-pre-wrap rounded-lg bg-zt-bg p-3 font-mono text-[12px] leading-relaxed text-slate-300">
                {text}
              </pre>
            ) : (
              <p className="rounded-lg bg-zt-bg p-3 text-sm text-zt-muted">
                No text was extracted. Try re-running OCR with a different provider.
              </p>
            )}
          </div>
        </div>
      )}
      {!loading && !detail && (
        <p className="text-sm text-zt-muted">This attachment is no longer available.</p>
      )}
    </Modal>
  );
}
